/**
 * WorkforceSection Component
 * Dynamic workforce entries for daily logs
 * BuilderBel Construction Project Management System
 */

import { ChevronDown, ChevronRight, Plus, Trash2, Users } from 'lucide-react';
import React from 'react';
import { Control, Controller, FieldErrors, UseFieldArrayReturn } from 'react-hook-form';
import type { WorkforceEntry } from '../../../types/dailyLog';
import { Input, NumberInput, TextArea } from '../../forms';

// Common trade options
const TRADE_OPTIONS = [
  'General Labor',
  'Carpenter',
  'Electrician',
  'Plumber',
  'HVAC',
  'Mason',
  'Iron Worker',
  'Concrete Finisher',
  'Painter',
  'Roofer',
  'Flooring',
  'Drywall',
  'Glazier',
  'Welder',
  'Equipment Operator',
  'Superintendent',
  'Safety Officer',
  'Other',
];

interface WorkforceSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldArray: UseFieldArrayReturn<any, 'workforce', 'id'>;
  isExpanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

export const WorkforceSection: React.FC<WorkforceSectionProps> = ({
  control,
  errors,
  fieldArray,
  isExpanded,
  onToggle,
  disabled = false,
}) => {
  const { fields, append, remove } = fieldArray;
  const workforceErrors = errors.workforce as unknown as FieldErrors<WorkforceEntry>[] | undefined;

  const handleAddEntry = () => {
    append({
      trade: '',
      company: '',
      workerCount: 1,
      hours: 8,
      workArea: '',
      supervisor: '',
      notes: '',
    });
  };

  // Calculate totals
  const totalWorkers = fields.reduce((sum, _, index) => {
    const count = (control._formValues?.workforce?.[index]?.workerCount as number) || 0;
    return sum + count;
  }, 0);

  const totalHours = fields.reduce((sum, _, index) => {
    const count = (control._formValues?.workforce?.[index]?.workerCount as number) || 0;
    const hours = (control._formValues?.workforce?.[index]?.hours as number) || 0;
    return sum + count * hours;
  }, 0);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Users className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900">Workforce</span>
          <span className="text-sm text-gray-500">
            ({fields.length} {fields.length === 1 ? 'entry' : 'entries'})
          </span>
        </div>
        <div className="flex items-center space-x-4">
          {fields.length > 0 && (
            <span className="text-sm text-gray-600">
              {totalWorkers} workers / {totalHours.toFixed(1)} man-hours
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {/* Entries */}
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4"
            >
              {/* Entry Header */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Entry #{index + 1}</span>
                {!disabled && fields.length > 0 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    title="Remove entry"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Trade & Company */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Controller
                  name={`workforce.${index}.trade`}
                  control={control}
                  render={({ field: inputField }) => (
                    <div>
                      <Input
                        label="Trade"
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={workforceErrors?.[index]?.trade?.message}
                        disabled={disabled}
                        required
                        placeholder="Select or enter trade"
                        list={`trade-options-${index}`}
                      />
                      <datalist id={`trade-options-${index}`}>
                        {TRADE_OPTIONS.map(trade => (
                          <option key={trade} value={trade} />
                        ))}
                      </datalist>
                    </div>
                  )}
                />

                <Controller
                  name={`workforce.${index}.company`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Input
                      label="Company/Subcontractor"
                      value={inputField.value}
                      onChange={inputField.onChange}
                      error={workforceErrors?.[index]?.company?.message}
                      disabled={disabled}
                      required
                      placeholder="Company name"
                    />
                  )}
                />
              </div>

              {/* Worker Count & Hours */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Controller
                  name={`workforce.${index}.workerCount`}
                  control={control}
                  render={({ field: inputField }) => (
                    <NumberInput
                      label="Workers"
                      value={inputField.value}
                      onChange={inputField.onChange}
                      error={workforceErrors?.[index]?.workerCount?.message}
                      disabled={disabled}
                      required
                      min={1}
                      max={500}
                    />
                  )}
                />

                <Controller
                  name={`workforce.${index}.hours`}
                  control={control}
                  render={({ field: inputField }) => (
                    <NumberInput
                      label="Hours/Worker"
                      value={inputField.value}
                      onChange={inputField.onChange}
                      error={workforceErrors?.[index]?.hours?.message}
                      disabled={disabled}
                      required
                      min={0.5}
                      max={24}
                      step={0.5}
                    />
                  )}
                />

                <Controller
                  name={`workforce.${index}.workArea`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Input
                      label="Work Area"
                      value={inputField.value || ''}
                      onChange={inputField.onChange}
                      error={workforceErrors?.[index]?.workArea?.message}
                      disabled={disabled}
                      placeholder="Location/zone"
                    />
                  )}
                />

                <Controller
                  name={`workforce.${index}.supervisor`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Input
                      label="Supervisor"
                      value={inputField.value || ''}
                      onChange={inputField.onChange}
                      error={workforceErrors?.[index]?.supervisor?.message}
                      disabled={disabled}
                      placeholder="Foreman name"
                    />
                  )}
                />
              </div>

              {/* Notes */}
              <Controller
                name={`workforce.${index}.notes`}
                control={control}
                render={({ field: inputField }) => (
                  <TextArea
                    label="Notes"
                    value={inputField.value || ''}
                    onChange={inputField.onChange}
                    error={workforceErrors?.[index]?.notes?.message}
                    disabled={disabled}
                    rows={2}
                    placeholder="Additional notes about this crew..."
                  />
                )}
              />
            </div>
          ))}

          {/* Add Entry Button */}
          {!disabled && (
            <button
              type="button"
              onClick={handleAddEntry}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-brand-500 hover:text-brand-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add Workforce Entry</span>
            </button>
          )}

          {/* Empty State */}
          {fields.length === 0 && disabled && (
            <div className="text-center py-6 text-gray-500">No workforce entries recorded</div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkforceSection;
